import {
  ChipSwitch,
  Grid,
  InfoTooltip,
  StatusTooltip,
  Switch,
  Typography,
} from 'app/design';
import { useFindInPage } from 'app/components/FindInPage';
import { GenericSettingsSwitchTemplate } from 'app/components/GenericSettingsSwitchTemplate';
import { useUpdateVmboxPartial } from 'app/hooks/mutations/vmbox';
import * as React from 'react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Vmbox, VmboxDoc } from 'types/vmbox';

interface AdditionalSettingProps {
  vmbox: Vmbox;
}

interface FormValues {
  transcribe: boolean;
}

const TranscribeVoicemails = ({ vmbox }: AdditionalSettingProps) => {
  const { ref, HighlightPulse, highlightPulseProps } = useFindInPage({
    name: 'transcribeVoicemails',
    options: [
      {
        label: 'Transcribe Voicemails',
        value: 'transcribeVoicemails',
      },
    ],
    onChoose: 'matchAnyScrollToRef',
  });

  const [showTooltip, setShowTooltip] = useState(false);
  const updateVmbox = useUpdateVmboxPartial();
  const {
    formState: { isDirty },
    watch,
    control,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: { transcribe: vmbox.doc.transcribe ?? false },
  });
  const watchEnabled = watch('transcribe');
  const statusRef = useRef(updateVmbox.status);
  statusRef.current = updateVmbox.status;

  // reset form if vmbox doc changes
  useEffect(() => {
    reset({ transcribe: vmbox.doc.transcribe ?? false });
  }, [vmbox]);

  // display switch tooltip based on status
  useEffect(() => {
    if (updateVmbox.status === 'loading') setShowTooltip(true);
    else if (showTooltip)
      setTimeout(
        // @ts-ignore
        () => statusRef.current !== 'loading' && setShowTooltip(false),
        1000,
      );
  }, [updateVmbox.status]);

  // trigger submit on switch trigger
  useEffect(() => {
    if (isDirty) {
      // only submit if form is dirty (we reset form on success, fail)
      handleSubmit(onSubmit)();
    }
  }, [watchEnabled]);

  const onSubmit = async (formValues: FormValues) => {
    await updateVmbox.mutateAsync(
      {
        id: vmbox.id,
        ...formValues,
      },
      {
        onSuccess: resp => {
          const { data }: { data: VmboxDoc } = resp;
          console.log('data', data);
          // reset form data (needed since this form is initialized once, not
          // - every time like when a dialog opens). We need to reset the default
          // - values which are the source of truth for error reset/isDirty value
          reset({ transcribe: data.transcribe ?? false });
        },
        onError: () => {
          reset(); // reset form to last valid state
          toast.error('Failed to update vmbox settings.');
        },
      },
    );
  };

  return (
    // @ts-ignore
    <div ref={ref}>
      <HighlightPulse {...highlightPulseProps} />
      <GenericSettingsSwitchTemplate
        title={'Transcribe Voicemails'}
        caption={'Convert audio to text using our transcription engine'}
      >
        <Controller
          control={control}
          name={'transcribe'}
          render={({ field: { onChange, value, ...props } }) => (
            <StatusTooltip
              status={updateVmbox.status}
              placement={'right'}
              open={showTooltip}
            >
              <Switch
                size="small"
                checked={value}
                onChange={e => onChange(e.target.checked)}
              />
            </StatusTooltip>
          )}
        />
      </GenericSettingsSwitchTemplate>
    </div>
  );
};

export default TranscribeVoicemails;
